import { useEffect, useState } from "react";
import { useAdminTranslation, useCommonTranslation } from "@/i18n/hooks";
import { Button } from "@/components/tailwind/button";
import { Input } from "@/components/tailwind/input";
import { Text } from "@/components/tailwind/text";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Pencil, Plus, Trash, XCircle, AlertCircle } from "lucide-react";
import { MobilityOption } from "@/models/mobility-option";
import LoadingSpinner from "@/components/loader-spinner";
import {
    createMobilityOption,
    deleteMobilityOption,
    fetchAllMobilityOptions,
    updateMobilityOption
} from "@/services/mobility-option-services";
import { useAuth } from "@/context/use-auth";
import ValidationAlert from "@/components/validation-alert";
import { useToast } from "@/hooks/use-toast";
import log from '@/lib/logger'

type MobilityOptionItemProps = {
    option: MobilityOption;
    editingId: string | null;
    handleStartEditing: (id: string) => void;
    handleSaveEditing: (id: string, name: string, description: string) => void;
    handleCancelEditing: () => void;
    handleDelete: (id: string) => void;
    t: (key: string, params?: Record<string, unknown>) => string;
    isUpdating: boolean;
    deleteDisabledReason: string | null;
};

function MobilityOptionItem({
    option,
    editingId,
    handleStartEditing,
    handleSaveEditing,
    handleCancelEditing,
    handleDelete,
    t,
    isUpdating,
    deleteDisabledReason,
}: MobilityOptionItemProps) {
    const [editName, setEditName] = useState(option.name);
    const [editDescription, setEditDescription] = useState(option.description || "");

    return (
        <div
            className="border border-zinc-200 dark:border-zinc-800 rounded-lg p-4 bg-white dark:bg-zinc-900 shadow-sm"
            data-testid={`mobility-option-item-${option.id}`}
        >
            {editingId === option.id ? (
                <div className="space-y-3">
                    <div className="flex items-center gap-4">
                        <div className="flex-grow">
                            <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                id={`edit-option-name-${option.id}`}
                                placeholder={t("adminSettings.mobilityOptions.namePlaceholder")}
                                data-testid={`edit-option-name-${option.id}`}
                            />
                        </div>
                    </div>
                    <div>
                        <Input
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            id={`edit-option-description-${option.id}`}
                            placeholder={t("adminSettings.mobilityOptions.descriptionPlaceholder")}
                            data-testid={`edit-option-description-${option.id}`}
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            plain
                            onClick={() => handleSaveEditing(option.id, editName, editDescription)}
                            data-testid={`save-edit-option-${option.id}`}
                        >
                            <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                        </Button>
                        <Button
                            plain
                            onClick={handleCancelEditing}
                            data-testid={`cancel-edit-option-${option.id}`}
                        >
                            <XCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        <Text className="text-zinc-900 dark:text-zinc-100 font-medium" data-testid={`option-name-${option.id}`}>
                            {option.name}
                        </Text>
                        {option.description && (
                            <Text className="text-sm text-zinc-500 dark:text-zinc-400 mt-1" data-testid={`option-description-${option.id}`}>
                                {option.description}
                            </Text>
                        )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                        <Button
                            plain
                            onClick={() => handleStartEditing(option.id)}
                            title={t("adminSettings.mobilityOptions.edit")}
                            disabled={isUpdating}
                            data-testid={`edit-option-${option.id}`}
                        >
                            <Pencil
                                className={`h-5 w-5 text-zinc-500 dark:text-zinc-400 ${!isUpdating ? "hover:text-blue-500 dark:hover:text-blue-400" : ""}`}
                            />
                        </Button>
                        <Button
                            plain
                            onClick={() => handleDelete(option.id)}
                            title={deleteDisabledReason || t("adminSettings.mobilityOptions.delete")}
                            disabled={!!deleteDisabledReason}
                            data-testid={`delete-option-${option.id}`}
                        >
                            <Trash
                                className={`h-5 w-5 ${deleteDisabledReason ? "text-zinc-300 dark:text-zinc-600 cursor-not-allowed" : "text-zinc-500 dark:text-zinc-400 hover:text-red-500 dark:hover:text-red-400"}`}
                            />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

interface SectionMobilityOptionsProps {
    isMissing?: boolean;
}

function SectionMobilityOptions({ isMissing = false }: SectionMobilityOptionsProps) {
    const { t } = useAdminTranslation();
    const { t: tCommon } = useCommonTranslation();
    const { token } = useAuth();
    const { toast } = useToast();

    const [options, setOptions] = useState<MobilityOption[]>([]);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [newName, setNewName] = useState<string>("");
    const [newDescription, setNewDescription] = useState<string>("");
    const [addingIssue, setAddingIssue] = useState<string | null>(null);
    const [editIssue, setEditIssue] = useState<string | null>(null);

    function showToast(title: string, description: string, variant: "default" | "destructive" = "default") {
        toast({
            title: title,
            description: description,
            variant: variant
        });
    }

    async function fetchMobilityOptions() {
        setIsUpdating(true);
        try {
            const result = await fetchAllMobilityOptions(token);
            if (result) {
                setOptions(result);
            }
        } catch (error) {
            log.error("Error fetching mobility options:", error);
        }
        setIsUpdating(false);
    }

    useEffect(() => {
        fetchMobilityOptions();
    }, []);

    function handleStartEditing(id: string) {
        setEditingId(id);
        setEditIssue(null);
    }

    function handleCancelEditing() {
        setEditingId(null);
        setEditIssue(null);
    }

    async function handleSaveEditing(id: string, name: string, description: string) {
        if (name.trim() === "") return;

        setIsUpdating(true);
        try {
            const result = await updateMobilityOption(token, id, {
                name: name.trim(),
                description: description.trim() || undefined
            });

            if (result) {
                showToast(
                    t("adminSettings.mobilityOptions.toast.updated.title"),
                    t("adminSettings.mobilityOptions.toast.updated.description", { optionName: name })
                );
                setEditingId(null);
                setEditIssue(null);
                await fetchMobilityOptions();
            }
        } catch (error: unknown) {
            log.error(`Error updating ${id}:`, error);
            const err = error as { validationIssues?: Array<{ message: string }>; status?: number; error?: string; message?: string };
            if (err.validationIssues) {
                setEditIssue(t(err.validationIssues[0].message));
            } else if (err.status) {
                setEditIssue(tCommon("serverError.responseCodeWithDetails", {
                    statusCode: err.status,
                    errorDetails: err.error
                }));
            } else {
                setEditIssue(tCommon("serverError.unknownWithDetails", { errorDetails: err.message }));
            }
        } finally {
            setIsUpdating(false);
        }
    }

    async function handleDelete(id: string) {
        setIsUpdating(true);
        try {
            await deleteMobilityOption(token, id);
            showToast(
                t("adminSettings.mobilityOptions.toast.deleted.title"),
                t("adminSettings.mobilityOptions.toast.deleted.description")
            );
            await fetchMobilityOptions();
        } catch (error: unknown) {
            log.error("Error deleting mobility option:", error);
            const err = error as { message?: string; memberCount?: number };

            if (err.memberCount) {
                showToast(
                    t("adminSettings.mobilityOptions.toast.deleteError.title"),
                    t("adminSettings.mobilityOptions.cannotDelete", { count: err.memberCount }),
                    "destructive"
                );
            } else {
                showToast(
                    t("adminSettings.mobilityOptions.toast.deleteError.title"),
                    t("adminSettings.mobilityOptions.toast.deleteError.description"),
                    "destructive"
                );
            }
        } finally {
            setIsUpdating(false);
        }
    }

    async function handleAddNewOption() {
        if (newName.trim() === "") return;

        setIsUpdating(true);
        try {
            const result = await createMobilityOption(token, {
                name: newName.trim(),
                description: newDescription.trim() || undefined
            });

            if (result) {
                showToast(
                    t("adminSettings.mobilityOptions.toast.created.title"),
                    t("adminSettings.mobilityOptions.toast.created.description", { optionName: result.name })
                );
                setNewName("");
                setNewDescription("");
                setIsAdding(false);
                setAddingIssue(null);
                await fetchMobilityOptions();
            }
        } catch (error: unknown) {
            log.error("Error creating mobility option:", error);
            const err = error as { validationIssues?: Array<{ message: string }>; status?: number; error?: string; message?: string };
            if (err.validationIssues) {
                setAddingIssue(t(err.validationIssues[0].message));
            } else if (err.status) {
                setAddingIssue(tCommon("serverError.responseCodeWithDetails", {
                    statusCode: err.status,
                    errorDetails: err.error
                }));
            } else {
                setAddingIssue(tCommon("serverError.unknownWithDetails", { errorDetails: err.message }));
            }
        } finally {
            setIsUpdating(false);
        }
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            {isUpdating && (
                <div className="absolute inset-0 bg-white/60 dark:bg-zinc-950/60 flex items-center justify-center z-10 rounded-lg">
                    <div className="bg-white dark:bg-zinc-900 p-4 rounded-lg shadow-lg flex items-center gap-3">
                        <LoadingSpinner />
                        <Text>{tCommon("loading")}</Text>
                    </div>
                </div>
            )}

            <CardHeader>
                <div className="flex justify-between items-start gap-4">
                    <div className="max-w-2xl">
                        <CardTitle className="text-xl font-bold flex items-center gap-2">
                            {t("adminSettings.mobilityOptions.title")}
                            {isMissing && (
                                <span
                                    title={t("adminSettings.missingConfiguration.tooltip")}
                                    aria-label={t("adminSettings.missingConfiguration.ariaLabel")}
                                >
                                    <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400" />
                                </span>
                            )}
                        </CardTitle>
                        <Text className="mt-1 text-sm text-muted-foreground">
                            {t("adminSettings.mobilityOptions.description")}
                        </Text>
                    </div>
                    {!isAdding && (
                        <Button
                            outline
                            onClick={() => setIsAdding(true)}
                            disabled={isUpdating}
                            data-testid="add-mobility-option-button"
                            className="whitespace-nowrap"
                        >
                            <Plus className="h-5 w-5 mr-2" data-slot="icon" />
                            {t("adminSettings.mobilityOptions.addNew")}
                        </Button>
                    )}
                </div>
            </CardHeader>

            {editIssue && (
                <ValidationAlert message={editIssue || tCommon("serverErrors.unknown")} />
            )}

            <CardContent>
                <div className="space-y-3 mb-6">
                    {options.map((option) => (
                        <MobilityOptionItem
                            key={option.id}
                            option={option}
                            editingId={editingId}
                            handleStartEditing={handleStartEditing}
                            handleSaveEditing={handleSaveEditing}
                            handleCancelEditing={handleCancelEditing}
                            handleDelete={handleDelete}
                            t={t}
                            isUpdating={isUpdating}
                            deleteDisabledReason={
                                option.memberCount > 0
                                    ? t("adminSettings.mobilityOptions.cannotDeleteTooltip", { count: option.memberCount })
                                    : null
                            }
                        />
                    ))}
                    {options.length === 0 && !isAdding && (
                        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded">
                            <Text className="text-blue-700 dark:text-blue-300">
                                {t("adminSettings.mobilityOptions.noItems")}
                            </Text>
                        </div>
                    )}
                </div>

                {isAdding && (
                    <>
                        <div className="space-y-3 mt-4 mb-2">
                            <Input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder={t("adminSettings.mobilityOptions.namePlaceholder")}
                                data-testid="mobility-option-name"
                            />
                            <Input
                                value={newDescription}
                                onChange={(e) => setNewDescription(e.target.value)}
                                placeholder={t("adminSettings.mobilityOptions.descriptionPlaceholder")}
                                data-testid="mobility-option-description"
                            />
                            <div className="flex gap-2">
                                <Button
                                    color="green"
                                    onClick={handleAddNewOption}
                                    data-testid="submit-mobility-option"
                                >
                                    {t("adminSettings.mobilityOptions.saveNew")}
                                </Button>
                                <Button
                                    outline
                                    onClick={() => {
                                        setNewName("");
                                        setNewDescription("");
                                        setIsAdding(false);
                                        setAddingIssue(null);
                                    }}
                                >
                                    {t("adminSettings.mobilityOptions.cancel")}
                                </Button>
                            </div>
                        </div>
                        {addingIssue && (
                            <ValidationAlert message={addingIssue} />
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}

export default SectionMobilityOptions;
